/**
 * @file documentDb.reducer.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Mar 14, 2022 12:56
 */
import lodash from 'lodash';

import { arrayDeepCopy, objectDeepCopy, objectHas } from 'src/@common/utilities';
import copyFields from 'src/store/workers/copyFields.js';

import * as SupplementActions from 'src/store/actions/supplement.actions';
import * as TableActions from 'src/store/actions/table.actions';

import * as Actions from 'src/store/actions/document/documentDb.actions';

const initialState = {
    documents: [],

    refreshDocuments: false,

    searchTerm: '',

    publicationSearchResult: {
        searchTerm: '',
        queryCount: 0,
        pageSize: 0,
        pageCount: 0,
        pageNo: 0,
        queryType: '',
        pageDocs: [],
        message: ''
    },

    supplementSearchResult: {
        searchTerm: '',
        queryCount: 0,
        pageSize: 0,
        pageCount: 0,
        pageNo: 0,
        queryType: '',
        pageDocs: [],
        message: ''
    },

    tableSearchResult: {
        searchTerm: '',
        queryCount: 0,
        pageSize: 0,
        pageCount: 0,
        pageNo: 0,
        queryType: '',
        pageDocs: [],
        message: ''
    },

    // server status
    serverErrorExists: false,
    serverFieldErrors: {},  // field errors from the server, passed into the form for display
    serverMessage: '', // message from server for display in toaster message
};

const documentDbReducer = (state = initialState, action) => {

    let docId = null;
    let foundIndex = null;
    let tmpDocuments = null;
    let tmpObj = null;

    switch (action.type) {

        // document.search
        // -------------------------------------------------------------------
        case Actions.DOCUMENT_SEARCH_TERM_SET:
            return {
                ...state,
                searchTerm: action.payload,
            };

        // document.search.publication
        // -------------------------------------------------------------------
        case Actions.DOCUMENT_SEARCH_PUBLICATION_REQUEST:
            return {
                ...state,
                publicationSearchResult: {},
                serverErrorExists: false,
                serverMessage: '',
                dataLoaded: false,
            };

        case Actions.DOCUMENT_SEARCH_PUBLICATION_SUCCESS:

            return {
                ...state,
                dataLoaded: true,
                publicationSearchResult: action.payload.queryResult,
                // serverErrorExists: action.payload.errorExists,
                // serverMessage: action.payload.serverMessage,
            };

        case Actions.DOCUMENT_SEARCH_PUBLICATION_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // document.search.supplement
        // -------------------------------------------------------------------
        case Actions.DOCUMENT_SEARCH_SUPPLEMENT_REQUEST:
            return {
                ...state,
                supplementSearchResult: {},
                serverErrorExists: false,
                serverMessage: '',
                dataLoaded: false,
            };

        case Actions.DOCUMENT_SEARCH_SUPPLEMENT_SUCCESS:

            return {
                ...state,
                dataLoaded: true,
                supplementSearchResult: action.payload.queryResult,
                // serverErrorExists: action.payload.errorExists,
                // serverMessage: action.payload.serverMessage,
            };

        case Actions.DOCUMENT_SEARCH_SUPPLEMENT_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // document.search.table
        // -------------------------------------------------------------------
        case Actions.DOCUMENT_SEARCH_TABLE_REQUEST:
            return {
                ...state,
                tableSearchResult: {},
                serverErrorExists: false,
                serverMessage: '',
                dataLoaded: false,
            };

        case Actions.DOCUMENT_SEARCH_TABLE_SUCCESS:

            return {
                ...state,
                dataLoaded: true,
                tableSearchResult: action.payload.queryResult,
                serverErrorExists: action.payload.errorExists,
                serverMessage: action.payload.serverMessage,
            };

        case Actions.DOCUMENT_SEARCH_TABLE_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // document.updateSearchItem
        // -------------------------------------------------------------------
        case Actions.DOCUMENT_UPDATE_SEARCH_ITEM_REQUEST:
            return {
                ...state,
            };

        case Actions.DOCUMENT_UPDATE_SEARCH_ITEM_SUCCESS:
            docId = action.payload.docId;

            tmpDocuments = arrayDeepCopy(state.documents);

            foundIndex = tmpDocuments.findIndex((item) => {
                return item._id === docId;
            });

            if (foundIndex !== -1) {
                tmpDocuments[foundIndex] = action.payload.document;
            }

            return {
                ...state,
                documents: tmpDocuments,
            };

        case Actions.DOCUMENT_UPDATE_SEARCH_ITEM_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // document.table.delete
        // -------------------------------------------------------------------
        case Actions.DOCUMENT_TABLE_DELETE_REQUEST:
            return {
                ...state,
            };

        case Actions.DOCUMENT_TABLE_DELETE_SUCCESS:
            docId = action.payload.tableId;

            tmpDocuments = state.documents.filter((item) => {
                return item._id !== docId;
            });

            return {
                ...state,
                documents: tmpDocuments,
            };

        case Actions.DOCUMENT_TABLE_DELETE_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // document.search.reset
        // -------------------------------------------------------------------
        case Actions.DOCUMENT_SEARCH_RESET:
            return {
                ...state,
                documents: [],
            };

        // // external reducer actions
        case TableActions.TABLE_UPDATE_SUCCESS:
            // need to find the changed table and update it
            // with the changeSet
            docId = action.payload.tableId;

            tmpObj = objectDeepCopy(state.tableSearchResult);

            foundIndex = tmpObj.pageDocs.findIndex((item) => {
                return item._id === docId;
            });

            if (foundIndex !== -1) {
                copyFields(action.payload.changeSet, tmpObj.pageDocs[foundIndex]);

                if (objectHas(tmpObj.pageDocs[foundIndex], 'rating')) {
                    tmpObj.pageDocs[foundIndex].rating = lodash.startCase(action.payload.changeSet.rating);
                }
            }

            return {
                ...state,
                tableSearchResult: tmpObj,
                serverErrorExists: action.payload.errorExists,
                serverMessage: action.payload.serverMessage,
            };

        case SupplementActions.SUPPLEMENT_UPDATE_SUCCESS:
            // need to find the changed table and update it
            // with the changeSet
            docId = action.payload.supplementId;

            tmpObj = objectDeepCopy(state.supplementSearchResult);

            foundIndex = tmpObj.pageDocs.findIndex((item) => {
                return item._id === docId;
            });

            if (foundIndex !== -1) {
                copyFields(action.payload.changeSet, tmpObj.pageDocs[foundIndex]);

                if (objectHas(tmpObj.pageDocs[foundIndex], 'rating')) {
                    tmpObj.pageDocs[foundIndex].rating = lodash.startCase(action.payload.changeSet.rating);
                }
            }

            return {
                ...state,
                supplementSearchResult: tmpObj,
                serverErrorExists: action.payload.errorExists,
                serverMessage: action.payload.serverMessage,
            };

        /************* */
        default: {
            return state;
        }
    }
}

export default documentDbReducer;